import { STORAGE_KEYS } from "@/constants";

/**
 * Organizational role on the logged-in company (`companies.role`: manufacturer | distributor),
 * from the same source as DashboardLayout — company payload stored under COMPANY_DATA.
 * @returns {"manufacturer"|"distributor"|""}
 */
export function readCompanyOrganizationalRoleFromStorage() {
  if (typeof window === "undefined") return "";
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
    const company = raw ? JSON.parse(raw) : null;
    const r = (company?.role ?? "").toString().toLowerCase().trim();
    if (r === "manufacturer" || r === "distributor") return r;
  } catch {
    /* ignore */
  }
  return "";
}

/**
 * Users who skip the company-role permission matrix (full app menu + routes).
 *
 * Policy (product):
 * - **Manufacturer / distributor primary logins** usually have **no** `company_role_id`
 *   → they bypass here and never load the matrix (no page-level RBAC).
 * - **Company-role employees** (Sales, etc.) have **`company_role_id` set** → matrix applies
 *   (`fetchMyRolePermissions`, `RoutePermissionGate`, `NavPermissionLink`).
 * - **Owners / platform admin / users without a company role assignment** also bypass.
 *
 * If a Manu/Dist account incorrectly has `company_role_id` set, they would be treated like
 * an employee; fix data or extend this function (e.g. combine org role + role name).
 */
export function shouldBypassPermissionMatrix(user) {
  if (!user || typeof user !== "object") return false;

  if (user.role_id === 1 || user.isAdmin === true) return true;
  const rn = (user.role_name || user.role || "").toString().toLowerCase();
  if (rn === "admin" || rn === "super admin") return true;

  if (user.company_role?.is_owner === true || user.companyRole?.is_owner === true) return true;

  const crId = user.company_role_id ?? user.companyRoleId;
  if (crId == null || crId === "") return true;

  return false;
}

export function readUserFromStorage() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getEffectiveUser(authUser) {
  return authUser || readUserFromStorage();
}

export function isSalesmanRole(user) {
  if (!user || typeof user !== "object") return false;
  return (
    user.companyRole?.is_salesman === true ||
    user.company_role?.is_salesman === true ||
    user.company_role_is_salesman === true
  );
}

/** Hardcoded salesman screens. Drill paths stay open so Sales can reach invoice details. */
const SALESMAN_ALLOWED_PREFIXES = [
  "/support",
  "/mfg-dashboard",
  "/tally-dashboard-v2",
  "/financial-dashboard",
  "/transactions/sales-v2",
  "/sales/distributors-v2",
  "/opsc-report",
  "/opsc",
  "/dump-report",
  "/sales/outlets2",
  "/sales/invoices2",
  "/sales/purchase-invoices2",
  "/invoices-v2",
  "/mfg-sync-status",
  "/my-profile",
  "/onboarding",
  "/otp",
];

export function isSalesmanPathAllowed(pathname) {
  if (!pathname) return true;
  const prefixes =
    readCompanyOrganizationalRoleFromStorage() === "distributor"
      ? SALESMAN_ALLOWED_PREFIXES.filter((p) => p !== "/sales/distributors-v2")
      : SALESMAN_ALLOWED_PREFIXES;
  return prefixes.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}
